// What the shop owes its suppliers, as the screens show it. Owed is always worked out
// from a bill's total and what active payments and credits cover; nothing is stored.
import {
  daysOverdue,
  supplierBillState,
  type SupplierBillRow as CoreBill,
  type SupplierCreditRow as CoreCredit,
  type SupplierPaymentRow as CorePayment,
} from '@ims/core'
import type {
  PaymentMethod,
  SupplierBillRow,
  SupplierBillSummary,
  SupplierCreditRow,
  SupplierPaymentRow,
} from '@ims/data'

/** Rows as clients write them (server-set columns dropped). */
export function supplierBillRow(row: SupplierBillRow): CoreBill {
  const { created_by: _by, server_updated_at: _at, ...rest } = row
  return rest
}
export function supplierPaymentRow(row: SupplierPaymentRow): CorePayment {
  const { created_by: _by, server_updated_at: _at, ...rest } = row
  return rest
}
export function supplierCreditRow(row: SupplierCreditRow): CoreCredit {
  const { created_by: _by, server_updated_at: _at, ...rest } = row
  return rest
}

export const PAY_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank', label: 'Bank' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'wallet', label: 'Wallet' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
]
export const payMethodLabel = (m: PaymentMethod) =>
  PAY_METHODS.find((x) => x.value === m)?.label ?? m

/** A date `days` after an ISO date. */
export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

export const billOwed = (b: Pick<SupplierBillSummary, 'bill' | 'coveredPaisa'>) =>
  Math.max(0, b.bill.total_paisa - b.coveredPaisa)
/** Whole days a bill is past its due date. A bill with no due date is never late. */
export const billLateDays = (bill: Pick<SupplierBillRow, 'due_date'>, today: string) =>
  bill.due_date ? daysOverdue(bill.due_date, today) : 0

export interface BillStatus {
  label: string
  tone: 'success' | 'warning' | 'neutral' | 'danger'
  owedPaisa: number
  /** Whole days past due while something is owed; 0 otherwise. */
  lateDays: number
}

export function billStatus(b: SupplierBillSummary, today: string): BillStatus {
  const owedPaisa = billOwed(b)
  const state = supplierBillState(b.bill.total_paisa, b.coveredPaisa)
  const lateDays = owedPaisa > 0 ? billLateDays(b.bill, today) : 0
  if (state === 'paid') return { label: 'Paid', tone: 'success', owedPaisa, lateDays: 0 }
  if (lateDays > 0)
    return {
      label: `${lateDays} day${lateDays === 1 ? '' : 's'} late`,
      tone: 'danger',
      owedPaisa,
      lateDays,
    }
  return state === 'part'
    ? { label: 'Part paid', tone: 'warning', owedPaisa, lateDays }
    : { label: 'Unpaid', tone: 'neutral', owedPaisa, lateDays }
}

/** "in 15 days", "today", or nothing once it is late (the badge says that). */
export function dueIn(bill: Pick<SupplierBillRow, 'due_date' | 'bill_date'>, today: string) {
  if (!bill.due_date) return ''
  const days = daysOverdue(today, bill.due_date)
  if (bill.due_date === today) return 'today'
  return days > 0 ? `in ${days} day${days === 1 ? '' : 's'}` : ''
}

export const PAYABLE_AGES = ['Not due yet', '1–30 days late', '31–60 days', 'Over 60 days'] as const

/** Open bills by supplier, split by how late each is. */
export function agePayables(bills: readonly SupplierBillSummary[], today: string) {
  const bySupplier = new Map<
    string,
    { supplierId: string; name: string; bills: number; ages: number[]; total: number }
  >()
  const totals = [0, 0, 0, 0]
  let total = 0
  for (const b of bills) {
    const owed = billOwed(b)
    if (owed <= 0) continue
    const late = billLateDays(b.bill, today)
    const age = late <= 0 ? 0 : late <= 30 ? 1 : late <= 60 ? 2 : 3
    const row = bySupplier.get(b.bill.supplier_id) ?? {
      supplierId: b.bill.supplier_id,
      name: b.supplierName ?? 'Supplier',
      bills: 0,
      ages: [0, 0, 0, 0],
      total: 0,
    }
    row.bills += 1
    row.ages[age]! += owed
    row.total += owed
    totals[age]! += owed
    total += owed
    bySupplier.set(b.bill.supplier_id, row)
  }
  const suppliers = [...bySupplier.values()].sort((a, b) => b.total - a.total)
  return { suppliers, totals, total }
}
